import { useState } from 'react'
import styled from '@emotion/styled'
import { useNavigate } from 'react-router-dom'
import { tokens } from '../../theme.js'
import { useAuth } from '../../auth/AuthContext.js'
import { initials } from '../common/initials.js'
import { ChangePasswordDialog } from '../common/dialogs.js'
import citadelLogo from '../../assets/citadel-logo.svg'
import { IconButton, Menu, MenuItem, Avatar } from '@mui/material'

const colors = tokens.colors
const fontFamily = tokens.fonts.body

const Shell = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  min-height: 100vh;
  background: ${colors.background.light};
  ${p => (p.$fill ? 'height: 100vh; overflow: hidden;' : '')}
`
const Bar = styled.header`
  display: flex;
  align-items: center;
  gap: 12px;
  height: 56px;
  padding: 0 32px;
  flex: 0 0 auto;
  background: ${colors.white};
  border-bottom: 1px solid ${colors.sectionDivider};
`
const Brand = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 0;
  & > img {
    display: block;
    width: auto;
    height: 38px;
  }
`
const Crumb = styled.button`
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 0;
  font: 400 14px/1 ${fontFamily};
  color: ${colors.text.muted};
  &:hover {
    color: ${colors.text.body};
  }
`
const Current = styled.span`
  font: 400 14px/1 ${fontFamily};
  color: ${colors.text.body};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 480px;
`
const Sep = styled.span`
  color: ${colors.sectionDivider};
  font: 400 16px/1 ${fontFamily};
`

export function AppShell({ children, crumbs = [{ label: 'Projects' }], fill = false }) {
  const { user, logout, changePassword } = useAuth()
  const navigate = useNavigate()
  const [changingPassword, setChangingPassword] = useState(false)
  const [profileAnchor, setProfileAnchor] = useState(null)
  return (
    <Shell $fill={fill}>
      <Bar>
        <Brand onClick={() => navigate('/')} data-testid="brand">
          <img src={citadelLogo} alt="Citadel" />
        </Brand>
        {crumbs.map((cr, i) => (
          <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
            <Sep>/</Sep>
            {i < crumbs.length - 1 ? (
              <Crumb onClick={() => navigate(cr.to || '/')} data-testid={cr.testId}>
                {cr.label}
              </Crumb>
            ) : (
              <Current data-testid={cr.testId}>{cr.label}</Current>
            )}
          </span>
        ))}
        <div style={{ flex: 1 }} />
        <IconButton
          onClick={e => setProfileAnchor(e.currentTarget)}
          data-testid="profile-menu"
          aria-label="Profile menu"
          aria-controls={profileAnchor ? 'profile-menu' : undefined}
          aria-haspopup="menu"
          aria-expanded={Boolean(profileAnchor)}
          title={user?.email}>
          <Avatar sx={{ bgcolor: colors.primaryExtraLight, color: colors.primary, typography: 'caption', fontWeight: 600 }}>{initials(user)}</Avatar>
        </IconButton>
        <Menu id="profile-menu" anchorEl={profileAnchor} open={Boolean(profileAnchor)} onClose={() => setProfileAnchor(null)}>
          <MenuItem disabled>{user?.email}</MenuItem>
          <MenuItem
            onClick={() => {
              setProfileAnchor(null)
              setChangingPassword(true)
            }}
            data-testid="nav-change-password">
            Change password
          </MenuItem>
          <MenuItem
            onClick={() => {
              setProfileAnchor(null)
              logout()
              navigate('/login')
            }}
            data-testid="nav-logout">
            Log out
          </MenuItem>
        </Menu>
      </Bar>
      <ChangePasswordDialog open={changingPassword} onChange={changePassword} onClose={() => setChangingPassword(false)} />
      {children}
    </Shell>
  )
}
